// Thin, defensive wrapper around the Telegram WebApp SDK. Everything degrades
// gracefully when opened outside Telegram (e.g. a browser during dev).

interface ThemeParams {
  bg_color?: string;
  text_color?: string;
  hint_color?: string;
  button_color?: string;
  button_text_color?: string;
  secondary_bg_color?: string;
}

interface TelegramWebApp {
  ready(): void;
  expand(): void;
  initData: string;
  initDataUnsafe?: { start_param?: string };
  colorScheme: "light" | "dark";
  themeParams: ThemeParams;
  platform?: string;
  showAlert?(message: string, callback?: () => void): void;
  HapticFeedback?: {
    impactOccurred(style: "light" | "medium" | "heavy"): void;
    notificationOccurred(type: "error" | "success" | "warning"): void;
  };
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

export const tg: TelegramWebApp | undefined = window.Telegram?.WebApp;

/** Boot Telegram: expand to full height and map theme params to CSS variables. */
export function initTelegram(): void {
  if (!tg) return;
  // ready()/expand() can throw WebAppMethodUnsupported on clients that lack the
  // method — never let that black-screen the app; theming below still applies.
  try {
    tg.ready();
    tg.expand();
  } catch {
    /* unsupported on this client — degrade gracefully */
  }
  const p = tg.themeParams;
  const root = document.documentElement;
  const map: Record<string, string | undefined> = {
    "--tg-bg": p.bg_color,
    "--tg-text": p.text_color,
    "--tg-hint": p.hint_color,
    "--tg-button": p.button_color,
    "--tg-button-text": p.button_text_color,
    "--tg-card": p.secondary_bg_color,
  };
  for (const [k, v] of Object.entries(map)) if (v) root.style.setProperty(k, v);
}

export function haptic(kind: "light" | "medium" | "heavy" = "light"): void {
  tg?.HapticFeedback?.impactOccurred(kind);
}

/**
 * PvP deep-link payload (`c_<code>` challenge / `f_<fightId>` replay), from
 * whichever transport this client used — triple fallback because start_param
 * delivery varies across Telegram clients:
 *  1. initDataUnsafe.start_param  (Mini App direct link t.me/<bot>/<app>?startapp=…)
 *  2. ?tgWebAppStartParam=…        (Telegram appends it to the WebView URL)
 *  3. ?startapp=…                  (our bot's /start fallback webApp button)
 */
export function getStartParam(): string | null {
  const unsafe = tg?.initDataUnsafe?.start_param;
  if (unsafe) return unsafe;
  const qs = new URLSearchParams(window.location.search);
  return qs.get("tgWebAppStartParam") ?? qs.get("startapp");
}

/** True when running inside Telegram (Mini App), false in a plain browser. */
export function isTelegram(): boolean {
  // telegram-web-app.js always defines WebApp, even in a plain browser — there
  // it reports platform "unknown" and empty initData. Only a real client
  // platform (or initData) means we're actually inside Telegram.
  return Boolean(tg && (tg.initData || (tg.platform && tg.platform !== "unknown")));
}

/**
 * True only on phone Telegram (android/ios) — the one place an xPortal
 * deep link can actually open the app. Desktop/web Telegram (tdesktop, macos,
 * weba, webk) would just open xportal.com in a browser tab, so those clients
 * must use the QR flow instead.
 */
export function isMobileTelegram(): boolean {
  return isTelegram() && (tg?.platform === "android" || tg?.platform === "ios");
}

// The public link a share points at — set to the bot's Telegram link via env.
// Empty keeps us from ever sharing a domain we don't own.
const SHARE_URL: string = import.meta.env.VITE_SHARE_URL ?? "";

// The Mini App's public web URL. Used as the X post's link/card (an owned
// domain, so its preview reads "Booga Brawl" instead of the bland bot card).
const WEB_URL: string = import.meta.env.VITE_WEB_URL ?? "https://brawl.devpops.in/";

// Shout-outs appended to every X post: project handles + campaign hashtag.
const X_SHOUTS = "@Boogas @CarlBoogas #BoogaBrawl";

/**
 * Send an event straight to the Telegram client, mirroring the transport in
 * telegram-web-app.js. We bypass WebApp.openTelegramLink()/openLink() because
 * both are known to silently no-op with share links on several clients
 * (see telegram-mini-apps issues #346/#712/#743). Returns false when no
 * Telegram host is present (e.g. a plain dev browser) so callers can fall back.
 */
function postEvent(eventType: string, eventData: Record<string, unknown>): boolean {
  const w = window as unknown as {
    TelegramWebviewProxy?: { postEvent(type: string, data: string): void };
    webkit?: { messageHandlers?: { performAction?: { postMessage(d: string): void } } };
    external?: { notify?(d: string): void };
  };
  const envelope = JSON.stringify({ eventType, eventData });
  if (w.TelegramWebviewProxy?.postEvent) {
    w.TelegramWebviewProxy.postEvent(eventType, JSON.stringify(eventData));
    return true;
  }
  if (w.webkit?.messageHandlers?.performAction) {
    w.webkit.messageHandlers.performAction.postMessage(envelope);
    return true;
  }
  if (w.external?.notify) {
    w.external.notify(envelope);
    return true;
  }
  if (window.parent && window.parent !== window) {
    window.parent.postMessage(envelope, "*");
    return true;
  }
  return false;
}

// Build a `url=…&text=…` query with %-encoding (not URLSearchParams' "+" for
// spaces, which Telegram's share parser handles less predictably).
function shareQuery(text: string, link: string = SHARE_URL): string {
  const parts = [`text=${encodeURIComponent(text)}`];
  if (link) parts.unshift(`url=${encodeURIComponent(link)}`);
  return parts.join("&");
}

/**
 * Open an external URL (e.g. an xPortal universal link carrying a WalletConnect
 * URI) via the Telegram client bridge — `openLink` no-ops on some clients, so we
 * use the same postEvent transport as shares. Falls back to window.open in a
 * plain browser. Returns whether a Telegram host handled it.
 */
export function openExternalLink(url: string, opts: { bridgeSafe?: boolean } = {}): boolean {
  haptic("light");
  // Mobile Telegram runs web_app_open_link through one decodeURIComponent before
  // handing the URL to the OS (see shareToX). For URLs whose correctness depends
  // on their percent-escapes (a deep link carrying another URL as a param),
  // pre-escape `%` on the mobile bridge path so that decode lands back on the
  // intended single-encoded URL. Desktop/web and the browser fallback get `url` as is.
  const bridgeUrl = opts.bridgeSafe && isMobileTelegram() ? url.replace(/%/g, "%25") : url;
  if (postEvent("web_app_open_link", { url: bridgeUrl })) return true;
  window.open(url, "_blank");
  return false;
}

/** Share a result via Telegram's forward sheet. `link` overrides the default
 *  share URL (e.g. a PvP challenge deep link). */
export function shareToTelegram(text: string, link?: string): void {
  haptic("light");
  const query = shareQuery(text, link ?? SHARE_URL);
  if (!postEvent("web_app_open_tg_link", { path_full: `/share/url?${query}` })) {
    window.open(`https://t.me/share/url?${query}`, "_blank"); // plain browser
  }
}

/** Best-effort clipboard write; silently ignored where unavailable. */
function copyText(text: string): void {
  try {
    void navigator.clipboard?.writeText(text);
  } catch {
    /* clipboard blocked — non-fatal, the intent prefill is the primary path */
  }
}

/** Share a result to X (Twitter) via the tweet-compose intent. */
export function shareToX(text: string): void {
  haptic("light");
  const body = `${text} ${X_SHOUTS}`;
  const q = shareQuery(body, WEB_URL);
  const intent = (query: string) => `https://twitter.com/intent/tweet?${query}`;
  // Mobile Telegram runs web_app_open_link through one decodeURIComponent before
  // handing the URL to the OS. That turns our %23 escapes (the # in the Booga's
  // name like "#594" and the #BoogaBrawl hashtag) into literal #, which X reads
  // as a URL fragment and truncates the post at the first one. Pre-escape the
  // percent signs on the mobile bridge path so that extra decode lands back on a
  // valid single-encoded intent. Desktop/web and the browser fallback are unaffected.
  const isMobileTg = tg?.platform === "android" || tg?.platform === "ios";
  const bridgeUrl = intent(isMobileTg ? q.replace(/%/g, "%25") : q);
  const open = () => {
    if (!postEvent("web_app_open_link", { url: bridgeUrl })) {
      window.open(intent(q), "_blank");
    }
  };
  // The X Android app currently has a bug where the prefilled composer can flash
  // and vanish on open. Copy the post as a safety net and tell the user once, so
  // they can paste even if the composer drops. (Remove once X ships their fix.)
  copyText(WEB_URL ? `${body} ${WEB_URL}` : body);
  // showAlert throws WebAppMethodUnsupported on clients without popup support
  // (plain browser, some desktop/web Telegram). Guard it so the share still
  // fires instead of bubbling to the global boot-error overlay.
  try {
    if (tg?.showAlert) {
      tg.showAlert("Post copied! If X opens blank, just paste it in. 🦴", open);
      return;
    }
  } catch {
    /* popup unsupported on this client — fall through to open directly */
  }
  open();
}
